
import React from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';

const HeroBackground: React.FC = () => {
  const { scrollY } = useScroll();
  
  // High-performance Parallax: Layers moving at different ratios relative to scroll
  // smoothY values provide that "heavy/luxurious" feel to the movement
  const y1 = useTransform(scrollY, [0, 1200], [0, 350]);
  const y2 = useTransform(scrollY, [0, 1200], [0, -250]);
  const y3 = useTransform(scrollY, [0, 1200], [0, 150]);
  
  const springConfig = { damping: 25, stiffness: 60 };
  const smoothY1 = useSpring(y1, springConfig);
  const smoothY2 = useSpring(y2, springConfig);
  const smoothY3 = useSpring(y3, springConfig);

  // Subtle rotation and scale transformations tied to scroll
  const rotate1 = useTransform(scrollY, [0, 1500], [0, 20]);
  const rotate2 = useTransform(scrollY, [0, 1500], [0, -15]);
  const scale1 = useTransform(scrollY, [0, 1000], [1, 1.1]);

  // Particle field
  const particles = Array.from({ length: 40 });

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none -z-10">
      {/* Foreground Blob (Fastest) */}
      <motion.div 
        style={{ y: smoothY1, rotate: rotate1, scale: scale1 }}
        className="blob absolute top-[-10%] left-[-15%] w-[70%] h-[70%] bg-rose-900/15 blur-[140px] rounded-full"
      />
      
      {/* Midground Blob (Reverse direction) */}
      <motion.div 
        style={{ y: smoothY2, rotate: rotate2 }}
        className="blob absolute top-[15%] right-[-10%] w-[60%] h-[60%] bg-blue-900/15 blur-[120px] rounded-full"
      />

      {/* Deep Background Blob (Slowest) */}
      <motion.div 
        style={{ y: smoothY3 }}
        className="blob absolute bottom-[-20%] left-[25%] w-[50%] h-[50%] bg-rose-500/5 blur-[100px] rounded-full"
      />

      {/* Grid Overlay */}
      <div 
        className="absolute inset-0 opacity-[0.08]" 
        style={{
          backgroundImage: `linear-gradient(#f43f5e 1px, transparent 1px), linear-gradient(90deg, #f43f5e 1px, transparent 1px)`,
          backgroundSize: '80px 80px',
          maskImage: 'radial-gradient(circle at 50% 50%, black, transparent 85%)'
        }}
      />

      {/* Floating Dynamics */}
      {particles.map((_, i) => {
        const size = Math.random() * 2 + 0.5;
        const color = Math.random() > 0.85 ? 'bg-blue-400' : 'bg-rose-500';
        return (
          <motion.div
            key={i}
            initial={{ 
              x: `${Math.random() * 100}%`, 
              y: `${Math.random() * 100}%`, 
              opacity: 0 
            }}
            animate={{
              y: [`${Math.random() * 100}%`, `${Math.random() * 100}%`],
              opacity: [0, Math.random() * 0.4 + 0.1, 0],
            }}
            transition={{
              duration: Math.random() * 20 + 10,
              repeat: Infinity,
              ease: "linear"
            }}
            className={`absolute rounded-full blur-[0.5px] ${color}`}
            style={{ width: `${size}px`, height: `${size}px` }}
          />
        );
      })}

      {/* Horizon Fade */}
      <div className="absolute bottom-0 left-0 w-full h-[500px] bg-[radial-gradient(circle_at_50%_100%,rgba(225,29,72,0.1)_0%,transparent_70%)]" />
    </div>
  );
};

export default HeroBackground;
